import { ContextualMenu, DirectionalHint, Icon, IContextualMenuItem, Label, MessageBar, MessageBarType, Stack, TextField, TooltipHost } from '@fluentui/react';
import React from 'react';

interface IGridHeaderProps {
    label: string;
    field: string;
    tooltip?: string;

    sortable?: boolean;
    sortField?: string;
    sortDirection?: string;
    onSort?: (column?: string, sortDirection?: string) => void;

    filterable?: boolean;
    filterValue?: string;
    filteredField?: string;
    filters?: { [key: string]: string };
    onFilterChange?: (filter?: string, column?: string) => void;
    onFiltersChange?: (filters: { [key: string]: string }) => void;
    filterMessageCondition?: (input: string) => boolean;
    filterMessage?: string;
    filterOptions?: { key: string, text: string }[] | undefined;
    filterField?: string;
    customFilterComponent?: (currentValue: string | undefined, onChange: (value?: string) => void) => React.ReactElement;
}

const GridHeader: React.FC<IGridHeaderProps> = (props) => {
    const divRef = React.useRef<HTMLDivElement>(null);
    const [showMenu, setShowMenu] = React.useState<boolean>(false);
    const [filterWarningMessage, setFilterWarningMessage] = React.useState<string>()
    const [options, setOptions] = React.useState<{ key: string, text: string }[] | undefined>()
    const [filterValue, setFilterValue] = React.useState<string>()
    const [sortDirection, setSortDirection] = React.useState<'Sort' | 'SortUp' | 'SortDown'>('Sort');

    const filterKey = props.filterField ?? props.field;

    React.useEffect(() => {
        if (props.filteredField === filterKey) {
            if (props.filterValue !== undefined) {
                setFilterValue(() => (props.filterValue))
            } else {
                setFilterValue(undefined);
            }
        } else if (props.filters?.[filterKey]) {
            setFilterValue(props.filters[filterKey])
        } else {
            setFilterValue(undefined);
        }
    }, [props.filterValue, props.filteredField, props.filters, filterKey]);

    React.useEffect(() => {
        if (props.filterOptions) {
            const options = [{ key: '', text: '[No Filter]' }, ...props.filterOptions];
            setOptions(options);
        }
    }, [props.filterOptions]);

    React.useEffect(() => {
        setSortDirection(props.sortField === props.field ? (props.sortDirection === 'asc' ? 'SortUp' : 'SortDown') : 'Sort')
    }, [props.sortDirection, props.sortField])

    const label = props.tooltip
        ? (
            <TooltipHost content={props.tooltip}>
                <div style={{ display: "inline-flex" }}>
                    <Label>
                        <Icon iconName={'Info'} style={{ paddingRight: '5px' }} />
                        {props.label}
                    </Label>
                </div>
            </TooltipHost>
        ) : (
            <Label>{props.label}</Label>
        );

    const changeFilter = (filter?: string) => {
        setFilterValue(filter);
        props.onFilterChange?.(filter, filterKey);
        if (props.filters) {
            const updatedFilters = filter ? { ...props.filters, [filterKey]: filter } : { ...props.filters };
            if (!filter) {
                delete updatedFilters[filterKey];
            }
            props.onFiltersChange?.(updatedFilters)
        }
    }

    const customFilterComponent = props.customFilterComponent;
    const menuItems: IContextualMenuItem[] = customFilterComponent
        ? [{
            key: 'customComponent',
            onRender: () => customFilterComponent(filterValue, (value) => {
                changeFilter(value);
                setShowMenu(false);
            })
        }]
        : options
        ? [
            ...options.map(option => ({
                key: option.key,
                text: option.text,
                onClick: () => {
                    changeFilter(option.key);
                }
            }))
         ]
        :[{
            key: 'custom',
            onRender: () => (
                <div style={{ padding: '1em' }}>
                    {filterWarningMessage &&
                        <MessageBar messageBarType={MessageBarType.blocked}>{filterWarningMessage}</MessageBar>}
                    <TextField
                        placeholder={'filter'}
                        value={filterValue}
                        onChange={(ev, val) => setFilterValue(val ?? '')}
                        onBlur={(ev) => {
                            if (props.filterMessageCondition && !props.filterMessageCondition(ev.currentTarget.value) && props.filterMessage) {
                                setFilterWarningMessage(props.filterMessage)
                                return
                            }
                            changeFilter(ev.target.value)
                            setFilterWarningMessage('')
                        }}
                        onKeyDown={(ev) => {
                            if (ev.key === 'Enter') {
                                ev.currentTarget.blur();
                            }
                        }}
                    />
                </div>
            )
        }];



    return (
        <div ref={divRef}>
            <Stack horizontal tokens={{childrenGap: '1em'}} verticalAlign='center'>
                {props.sortable && (
                <div style={{ cursor: 'pointer' }} onClick={() => {
                    const direction = props.sortDirection === "asc" ? 'desc' : 'asc'
                    if (props.onSort) props.onSort(props.field, direction) 
                }}>
                    <Icon
                        iconName={sortDirection}
                        className={"ac-sortable-vendor-header-icon"}
                        style={{ paddingRight: '1px' }}
                    />
                </div>)}
                {label}
                {props.filterable && (
                    <div onClick={() => setShowMenu(true)}>
                        <Icon
                            iconName={filterValue ? 'FilterSolid' : 'Filter'}
                            className={"ac-filter-vendor-header-icon"}
                            style={{cursor: 'pointer'}}
                        />
                    </div>
                )}
                <ContextualMenu items={menuItems} hidden={!showMenu} target={divRef.current} onDismiss={() => setShowMenu(false)} directionalHint={DirectionalHint.bottomLeftEdge} />
            </Stack>
        </div>
    )
}

export default GridHeader;